// EvsiqSurveyorLinksCard — who can open this building's compliance record without signing in
// (Ben 27 Oct: links on record; the kiosk's own link renews daily and lasts 7 days; the account
// manager gives one for 7, 30 or 90 days or 1 year and cancels one at a time). Demo OK'd 28 Oct.
//
// Shown only on a building with rooms on EVSiQ, and only to the person EVSiQ's public-links function
// accepts for it (the building's AM, full AM authority, a superuser): anyone it refuses never sees the
// box. A real load error is shown, never swallowed into an empty card (STATE, decision 79).
import { useCallback, useEffect, useState } from 'react';
import { Link2, X } from 'lucide-react';
import { Button } from '../../../shared/components';
import { useAuth } from '../../../shared/auth';
import { isFieldSeat } from '../../../shared/role-gates';
import { overlayClickClose } from '../../../shared/overlay-close';
import { errorMessage } from '../../../shared/errors';
import {
  LINK_LENGTHS, NotYoursError, cancelSurveyorLink, fmtExpiry, issueSurveyorLink, listSurveyorLinks,
  type SurveyorLink, type SurveyorLinks,
} from '../lib/evsiq-public-links';

const label = 'text-[10px] font-semibold uppercase tracking-[0.08em] text-tcs-text-muted';
const field = 'mt-1 w-full rounded border border-tcs-border bg-tcs-field px-2 py-1.5 text-sm text-tcs-text focus:border-tcs-accent focus:outline-none';

export function EvsiqSurveyorLinksCard({ facilityId, buildingLabel }: { facilityId: string; buildingLabel: string }) {
  const { profile } = useAuth();
  const seat = isFieldSeat(profile);
  const [data, setData] = useState<SurveyorLinks | null>(null);
  const [hidden, setHidden] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [cancelling, setCancelling] = useState<string | null>(null);
  const [rowError, setRowError] = useState<string | null>(null);

  const load = useCallback(() => {
    listSurveyorLinks(facilityId)
      .then((d) => { setData(d); setLoadError(null); })
      .catch((e) => {
        if (e instanceof NotYoursError) setHidden(true);
        else setLoadError(errorMessage(e, 'EVSiQ could not be reached.'));
      });
  }, [facilityId]);

  useEffect(() => { if (seat) load(); }, [seat, load]);

  if (!seat || hidden) return null;
  if (loadError) {
    return (
      <div className="rounded-xl border border-tcs-border bg-tcs-bg p-4 text-sm text-tcs-text-muted" data-testid="evsiq-links-card-error">
        Surveyor links (EVSiQ): {loadError}
      </div>
    );
  }
  if (!data?.on_evsiq) return null;

  async function cancel(id: string) {
    setCancelling(id);
    setRowError(null);
    try {
      await cancelSurveyorLink(facilityId, id);
      load();
    } catch (e) {
      setRowError(errorMessage(e, 'The link could not be cancelled.'));
    } finally {
      setCancelling(null);
    }
  }

  const links = data.links ?? [];
  return (
    <div className="rounded-xl border border-tcs-border bg-tcs-bg p-5" data-testid="evsiq-links-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="flex items-center gap-2 text-sm font-semibold text-tcs-text">
            <Link2 className="h-4 w-4" /> Surveyor links (EVSiQ)
          </h3>
          <p className="mt-1 text-xs text-tcs-text-muted">
            Who can open {buildingLabel}'s compliance record without signing in. The kiosk's own link renews
            itself every day and lasts 7 days.
          </p>
        </div>
        <Button variant="secondary" size="sm" onClick={() => setOpen(true)} data-testid="evsiq-links-get">Get a link</Button>
      </div>
      <LinksTable links={links} cancelling={cancelling} onCancel={(id) => void cancel(id)} />
      {rowError && <p className="mt-2 rounded bg-st-danger-bg px-3 py-2 text-sm text-st-danger-fg-strong">{rowError}</p>}
      <p className="mt-2 text-xs text-tcs-text-muted">Cancelled and expired links stop working at once and drop off this list.</p>
      {open && <GetLinkModal facilityId={facilityId} buildingLabel={buildingLabel} onClose={() => { setOpen(false); load(); }} />}
    </div>
  );
}

/** The list of active links: the kiosk's (no Cancel) and the administrators' (Cancel). */
export function LinksTable({ links, cancelling, onCancel }: {
  links: SurveyorLink[]; cancelling: string | null; onCancel: (id: string) => void;
}) {
  return (
      <table className="mt-3 w-full text-sm">
      <thead>
        <tr className="text-left text-xs text-tcs-text-muted">
          <th className="py-1.5 font-semibold">For</th>
          <th className="py-1.5 font-semibold">Given by</th>
          <th className="py-1.5 font-semibold">Expires</th>
          <th />
        </tr>
      </thead>
      <tbody>
        {links.map((l) => (
          <tr key={l.id} className="border-t border-tcs-border text-tcs-text" data-testid="evsiq-link-row">
            <td className="py-2">
              <span className={`mr-2 rounded px-1.5 py-0.5 text-[11px] font-semibold ${l.kind === 'kiosk' ? 'bg-st-info-bg text-st-info-fg-strong' : 'bg-st-warn-bg text-st-warn-fg-strong'}`}>
                {l.kind === 'kiosk' ? 'Kiosk' : 'Link'}
              </span>
              {l.kind === 'kiosk' ? 'Kiosk (renews daily)' : l.issued_to}
            </td>
            <td className="py-2">{l.issued_by_name ?? '—'}</td>
            <td className="py-2">{fmtExpiry(l.expires_at)}</td>
            <td className="py-2 text-right">
              {l.kind === 'administrator' && (
                <Button variant="destructive" size="sm" disabled={cancelling === l.id} onClick={() => onCancel(l.id)} data-testid="evsiq-link-cancel">
                  {cancelling === l.id ? 'Cancelling…' : 'Cancel'}
                </Button>
              )}
            </td>
          </tr>
        ))}
        {links.length === 0 && (
          <tr><td colSpan={4} className="py-2 text-tcs-text-muted">No active links. The kiosk gets its own the next time it connects.</td></tr>
        )}
      </tbody>
    </table>
  );
}

export function GetLinkModal({ facilityId, buildingLabel, onClose }: { facilityId: string; buildingLabel: string; onClose: () => void }) {
  const [who, setWho] = useState('');
  const [days, setDays] = useState(365);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [url, setUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  async function create() {
    if (!who.trim()) return setError('Who is the link for? Give a name and/or email.');
    setSaving(true);
    setError(null);
    try {
      setUrl((await issueSurveyorLink(facilityId, who.trim(), days)).url);
    } catch (e) {
      setError(errorMessage(e, 'The link could not be made.'));
    } finally {
      setSaving(false);
    }
  }

  async function copy() {
    if (!url) return;
    try { await navigator.clipboard.writeText(url); setCopied(true); } catch { setCopied(false); }
  }

  return (
    <div role="dialog" aria-modal="true" className="fixed inset-0 z-50 flex items-center justify-center bg-tcs-scrim/60 p-4" {...overlayClickClose(onClose)}>
      <div className="w-full max-w-md rounded-xl border border-tcs-border bg-tcs-overlay p-5 shadow-lg" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-base font-semibold text-tcs-text">Get a surveyor link</h3>
            <p className="mt-1 text-xs text-tcs-text-muted">{buildingLabel}</p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="rounded p-1 text-tcs-text-muted hover:bg-tcs-surface hover:text-tcs-text">
            <X className="h-4 w-4" />
          </button>
        </div>
        {url ? (
          <div className="mt-4 space-y-3" data-testid="evsiq-link-made">
            <p className="break-all rounded bg-st-ok-bg px-3 py-2 text-sm text-st-ok-fg-strong">{url}</p>
            <p className="text-xs text-tcs-text-muted">Nothing is emailed. Copy the link and send it yourself.</p>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={() => void copy()} data-testid="evsiq-link-copy">{copied ? 'Copied ✓' : 'Copy link'}</Button>
              <Button variant="primary" size="sm" onClick={onClose}>Done</Button>
            </div>
          </div>
        ) : (
          <div className="mt-4 space-y-3">
            <div>
              <label htmlFor="esl-who" className={label}>For (name and/or email)</label>
              <input id="esl-who" value={who} onChange={(e) => setWho(e.target.value)} placeholder="Dana Ruiz — Administrator" className={field} />
            </div>
            <div>
              <label htmlFor="esl-len" className={label}>Lasts</label>
              <select id="esl-len" value={days} onChange={(e) => setDays(Number(e.target.value))} className={field}>
                {LINK_LENGTHS.map((l) => <option key={l.days} value={l.days}>{l.label}</option>)}
              </select>
            </div>
            {error && <p className="rounded bg-st-danger-bg px-3 py-2 text-sm text-st-danger-fg-strong" data-testid="evsiq-link-error">{error}</p>}
            <div className="flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={onClose}>Cancel</Button>
              <Button variant="primary" size="sm" disabled={saving} onClick={() => void create()} data-testid="evsiq-link-create">
                {saving ? 'Making…' : 'Create link'}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
